import { Box, Tooltip, Typography } from "@mui/material";
import { People as FamilyIcon } from "@mui/icons-material";
import { ClaimantProjection } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import type { YtdMismatch } from "../../utils/ytdReconciliation";

interface YTDForecastCellProps {
  /** The projection for this record; undefined when projection hasn't loaded. */
  projection: ClaimantProjection | undefined;
  /** True while the projection endpoint is fetching. */
  isLoading: boolean;
  /**
   * Reported-YTD vs claim-lines gap for this row (#7432 AC5). Renders a warning
   * sub-line "Claim lines +$1,147.25" under the reported total; absent on
   * matching rows and on documents without the check.
   */
  mismatch?: YtdMismatch | null;
}

/**
 * Single YTD figure per claimant (#7435 AC4): `ytd_after`, which the projection
 * (`computeYTDAfter`) computes as what is already on file plus this document's
 * claim lines when the record has any with positive paid, and otherwise as the
 * carrier's reported total — itemized wins when both are present. (The loader
 * resolves authority by paid-through date with ties to reported, so a file
 * carrying both figures can load differently from this forecast; !4927 review.)
 * The before → after pair this cell used to show moved into the tooltip; the
 * reviewer's question is "what will the official total be", not the arithmetic.
 *
 * When the policy has a family deductible and the claimant is grouped, the
 * family aggregate renders as a secondary line with a family icon.
 */
export const YTDForecastCell = ({ projection, isLoading, mismatch }: YTDForecastCellProps) => {
  if (isLoading) {
    return (
      <Typography variant="body2" color="text.disabled">
        …
      </Typography>
    );
  }
  // The reconciliation sub-line stands on its own: it compares two figures in
  // the FILE, so it is still true when the forecast is unavailable (skipped or
  // windowed projection) and the figure above it is a dash.
  const mismatchLine = mismatch ? (
    <Tooltip
      title={`Reported ${formatUSD(mismatch.reported)}; the claim lines in this file add up to ${formatUSD(mismatch.lines)}.`}
      placement="top"
      arrow
    >
      <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 600, lineHeight: 1.3 }}>
        Claim lines {mismatch.diff > 0 ? "+" : "−"}
        {formatUSD(Math.abs(mismatch.diff))}
      </Typography>
    </Tooltip>
  ) : null;

  if (!projection) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
        {mismatchLine}
      </Box>
    );
  }

  const hasChange = projection.ytd_delta !== 0;
  const tooltip = hasChange
    ? `${formatUSD(projection.ytd_before)} on file before this document, ${projection.ytd_delta > 0 ? "+" : "−"}${formatUSD(Math.abs(projection.ytd_delta))} from it.`
    : "No YTD change from this document.";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Tooltip title={tooltip} placement="top" arrow>
        <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
          {formatUSD(projection.ytd_after)}
        </Typography>
      </Tooltip>
      {mismatchLine}
      {typeof projection.family_ytd_before === "number" &&
        typeof projection.family_ytd_after === "number" &&
        // Suppress the family line when it equals the individual line — that's
        // a family of one (lone EE on family-ded policy) or a multi-member
        // family where this row is the only contributor.
        (projection.family_ytd_before !== projection.ytd_before ||
          projection.family_ytd_after !== projection.ytd_after) && (
          <Tooltip title="Family total across linked members" placement="top" arrow>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <FamilyIcon sx={{ fontSize: 12, color: "text.disabled" }} />
              <Typography variant="caption" color="text.secondary">
                Family {formatUSD(projection.family_ytd_after)}
              </Typography>
            </Box>
          </Tooltip>
        )}
    </Box>
  );
};
